<script setup lang="ts">
import { useI18n } from '@/i18n';
import { logError } from '@/lib/logError';
import { useAccountsStore } from '@/stores/accounts';
import { useSidebarStore, type SidebarOption } from '@/stores/sidebar';
import { useUserStore } from '@/stores/user';
import { useToast } from '@/use/toast';
import { Avatar, Badge, Block, Icon, Menu, MenuDivider, MenuHeading, MenuItem } from '@dabble/quillui';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onUnmounted, ref, useId, watch } from 'vue';
import { useRoute } from 'vue-router';
import dabbleWordmark from './dabble-logo.svg?raw';

const { t } = useI18n();
const sidebarStore = useSidebarStore();
const accountsStore = useAccountsStore();
const userStore = useUserStore();
const route = useRoute();
const { options } = storeToRefs(sidebarStore);
const { otherAccounts, isImpersonating } = storeToRefs(accountsStore);
const { showToast } = useToast();

const emit = defineEmits<{ navigate: [] }>();

const impersonatedName = computed(() => userStore.user.name || userStore.user.email || userStore.user.uid || '');

// The options list is positional: [0] is always "Go To Dabble Writer" and the
// rest varies (Password is omitted for SSO-only accounts), so pick the list
// apart by shape rather than by fixed index.
const appOption = computed(() => options.value[0]);
const navOptions = computed(() =>
  options.value.slice(1).filter((option) => option.goto && option.textKey !== 'sidebar_sign_out'),
);
const signOutOption = computed(() => options.value.find((option) => option.textKey === 'sidebar_sign_out'));

const switchOpen = ref(false);
// Stable ids so the identity button can name itself from its visible name/email
// (aria-labelledby) and own the disclosure panel (aria-controls).
const switchPanelId = useId();
const identityNameId = useId();
const identityEmailId = useId();
const identityButton = ref<HTMLButtonElement | null>(null);

function toggleSwitch() {
  if (switchOpen.value) closeSwitch({ restoreFocus: true });
  else switchOpen.value = true;
}

function closeSwitch({ restoreFocus = false } = {}) {
  if (!switchOpen.value) return;
  switchOpen.value = false;
  if (restoreFocus) identityButton.value?.focus();
}

function onDocumentKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') closeSwitch({ restoreFocus: true });
}
function onDocumentPointerDown(event: Event) {
  const target = event.target as Node | null;
  if (!target) return;
  if (identityButton.value?.contains(target)) return;
  if (document.getElementById(switchPanelId)?.contains(target)) return;
  closeSwitch();
}
watch(switchOpen, (open) => {
  if (open) {
    document.addEventListener('keydown', onDocumentKeydown);
    document.addEventListener('pointerdown', onDocumentPointerDown);
    // Move focus into the panel so keyboard users land on the account list.
    nextTick(() => {
      const focusable = document
        .getElementById(switchPanelId)
        ?.querySelector<HTMLElement>('button, [href], input, [tabindex]:not([tabindex="-1"])');
      focusable?.focus();
    });
  } else {
    document.removeEventListener('keydown', onDocumentKeydown);
    document.removeEventListener('pointerdown', onDocumentPointerDown);
  }
});
onUnmounted(() => {
  document.removeEventListener('keydown', onDocumentKeydown);
  document.removeEventListener('pointerdown', onDocumentPointerDown);
});

async function selectOption(option: SidebarOption) {
  // The sidebar is persistent, so a nav click must not leave the panel open.
  closeSwitch();
  if (option.preAction) {
    try {
      await option.preAction();
    } catch (error) {
      logError('Error in preAction', error);
      showToast('danger', t('sidebar_error_preaction', { name: t(option.textKey) }));
      return;
    }
  }
  sidebarStore.selectOption(options.value.indexOf(option));
  emit('navigate');
}

function switchTo(uid: string) {
  switchOpen.value = false;
  accountsStore.switchAccount(uid);
  emit('navigate');
}

function addAnotherAccount() {
  const redirect = encodeURIComponent(`${location.origin}${route.fullPath}`);
  window.location.href = `/auth/login?redirect=${redirect}`;
}

function stopImpersonating() {
  switchOpen.value = false;
  accountsStore.stopImpersonating();
}

function handleHelp() {
  window.open('https://help.dabblewriter.com/en/', '_blank');
  emit('navigate');
}

function handleStoryCraftCafe() {
  window.open('https://www.storycraft.cafe/feed', '_blank');
  emit('navigate');
}

function requestAFeature() {
  window.open('https://dabble.featureupvote.com/', '_blank');
  emit('navigate');
}
</script>

<template>
  <Block variant="glass" border-radius="large" padding="small" class="account-sidebar">
    <div class="account-sidebar__inner">
      <button
        v-if="isImpersonating"
        type="button"
        class="account-sidebar__impersonate"
        :title="t('account_stop_impersonating')"
        :aria-label="t('account_stop_impersonating')"
        @click="accountsStore.stopImpersonatingToAdmin"
      >
        <Badge variant="warning" size="small" icon-right="sign-out">
          {{ t('account_impersonating_badge', { name: impersonatedName }) }}
        </Badge>
      </button>

      <div class="account-sidebar__brand" role="img" aria-label="Dabble" v-html="dabbleWordmark" />

      <button v-if="appOption" type="button" class="account-sidebar__back" @click="selectOption(appOption)">
        <Icon name="arrow-left" size="xsmall" />
        {{ t(appOption.textKey) }}
      </button>

      <button
        ref="identityButton"
        type="button"
        class="account-sidebar__identity"
        :aria-expanded="switchOpen"
        aria-haspopup="true"
        :aria-controls="switchOpen ? switchPanelId : undefined"
        :aria-labelledby="`${identityNameId} ${identityEmailId}`"
        @click="toggleSwitch"
      >
        <Avatar
          :name="userStore.user.name"
          :email="userStore.user.email"
          :url="userStore.user.photoUrl"
          size="xxlarge"
        />
        <span class="account-sidebar__identity-meta">
          <span :id="identityNameId" class="account-sidebar__identity-name">
            {{ userStore.user.name || userStore.user.email }}
          </span>
          <span :id="identityEmailId" class="account-sidebar__identity-email">{{ userStore.user.email }}</span>
        </span>
        <Icon name="chevron-down" size="small" class="account-sidebar__identity-chevron" />
      </button>

      <Block
        v-if="switchOpen"
        :id="switchPanelId"
        variant="glass"
        border-radius="medium"
        padding="xxxsmall"
        class="account-sidebar__switch"
      >
        <Menu>
          <template v-if="otherAccounts.length">
            <MenuHeading borderless>
              {{ t('sidebar_switch_account') }}
            </MenuHeading>
            <MenuItem
              v-for="account in otherAccounts"
              :key="account.uid"
              icon-left="user-2"
              @click="switchTo(account.uid)"
            >
              {{ account.name || account.email }}
            </MenuItem>
            <MenuDivider />
          </template>
          <MenuItem icon-left="plus-circle" @click="addAnotherAccount">
            {{ t('account_login_another') }}
          </MenuItem>
          <template v-if="isImpersonating">
            <MenuDivider />
            <MenuItem icon-left="sign-out" @click="stopImpersonating">
              {{ t('account_stop_impersonating') }}
            </MenuItem>
          </template>
        </Menu>
      </Block>

      <div class="account-sidebar__heading">{{ t('sidebar_title') }}</div>
      <nav class="account-sidebar__nav" :aria-label="t('sidebar_title')">
        <button
          v-for="option in navOptions"
          :key="option.textKey"
          type="button"
          class="account-sidebar__nav-item"
          :aria-current="option.selected ? 'page' : undefined"
          @click="selectOption(option)"
        >
          <Icon :name="option.icon" size="small" class="account-sidebar__nav-icon" />
          {{ t(option.textKey) }}
        </button>
      </nav>

      <div class="account-sidebar__spacer" />

      <div class="account-sidebar__footer">
        <MenuDivider />
        <Menu class="account-sidebar__menu account-sidebar__menu--footer">
          <MenuItem icon-left="help" icon-right="external" @click="handleHelp">
            {{ t('sidebar_help_center') }}
          </MenuItem>
          <MenuItem icon-left="graduation-cap" icon-right="external" @click="handleStoryCraftCafe">
            {{ t('sidebar_story_craft_cafe') }}
          </MenuItem>
          <MenuItem icon-left="send" icon-right="external" @click="requestAFeature">
            {{ t('sidebar_request_feature') }}
          </MenuItem>
          <MenuItem v-if="signOutOption" :icon-left="signOutOption.icon" @click="selectOption(signOutOption)">
            {{ t(signOutOption.textKey) }}
          </MenuItem>
        </Menu>
      </div>
    </div>
  </Block>
</template>

<style lang="scss" scoped>
.account-sidebar {
  width: 272px;
  box-sizing: border-box;
}

.account-sidebar__inner {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  width: 100%;
}

.account-sidebar__brand {
  display: flex;
  justify-content: flex-start;
  padding: space('xxsmall') space('xsmall') 0;
  color: theme('color-glass-text');

  :deep(svg) {
    height: 36px;
    width: auto;
    display: block;
    filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.08));
  }
}

.account-sidebar__back {
  display: inline-flex;
  align-items: center;
  gap: space('xxxsmall');
  align-self: flex-start;
  margin: space('xxsmall') 0 space('small') space('tiny');
  padding: space('tiny') space('xsmall') space('tiny') space('xxsmall');
  border: none;
  border-radius: borderRadius('large');
  background: transparent;
  color: theme('color-glass-text-secondary');
  font-size: fontSize('xsmall');
  font-weight: fontWeight('semibold');
  cursor: pointer;

  &:hover {
    background: theme('color-bg-interactive-hover');
    color: theme('color-glass-text');
  }
}

.account-sidebar__identity {
  display: flex;
  align-items: center;
  gap: space('xsmall');
  width: 100%;
  padding: space('xxsmall');
  border: 1px solid theme('color-glass-edge');
  border-radius: borderRadius('medium');
  background: theme('color-glass-bg');
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background-image: linear-gradient(theme('color-glass-highlight-subtle'), theme('color-glass-highlight-subtle'));
  }
}

.account-sidebar__identity-meta {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: space('xtiny');
}

.account-sidebar__identity-name {
  font-size: fontSize('small');
  font-weight: fontWeight('semibold');
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.account-sidebar__identity-email {
  font-size: fontSize('xxsmall');
  color: theme('color-glass-text-secondary');
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.account-sidebar__identity-chevron {
  color: theme('color-glass-text-secondary');
  transition: transform 0.15s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
}

.account-sidebar__identity[aria-expanded='true'] .account-sidebar__identity-chevron {
  transform: rotate(180deg);
}

.account-sidebar__switch {
  margin-top: space('xxxsmall');
}

.account-sidebar__heading {
  font-size: fontSize('xxsmall');
  font-weight: fontWeight('bold');
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: theme('color-glass-text-secondary');
  padding: 0 space('xsmall');
  margin: space('medium') 0 space('xxxsmall');
}

.account-sidebar__nav {
  display: flex;
  flex-direction: column;
  gap: space('xtiny');
}

.account-sidebar__nav-item {
  display: flex;
  align-items: center;
  gap: space('xxsmall');
  width: 100%;
  padding: space('xxsmall') space('xsmall');
  border: none;
  border-radius: borderRadius('small');
  background: transparent;
  color: theme('color-glass-text');
  font-family: inherit;
  font-size: fontSize('small');
  font-weight: fontWeight('medium');
  text-align: left;
  cursor: pointer;

  &:hover {
    background: theme('color-bg-interactive-hover');
  }

  &[aria-current='page'] {
    background: theme('color-bg-primary-interactive-active');
    color: theme('color-fg-primary-interactive');
    font-weight: fontWeight('semibold');

    .account-sidebar__nav-icon {
      color: theme('color-fg-primary-interactive');
    }
  }
}

.account-sidebar__nav-icon {
  color: theme('color-glass-text-secondary');
  flex: none;
}

.account-sidebar__menu {
  width: 100%;
}

.account-sidebar__menu--footer {
  font-size: fontSize('xsmall');
}

.account-sidebar__spacer {
  flex: 1 1 space('medium');
  min-height: space('medium');
}

.account-sidebar__footer {
  padding-top: space('xxsmall');
  width: 100%;
}

.account-sidebar__impersonate {
  display: flex;
  padding: space('xxsmall') space('xsmall');
  margin-bottom: space('xxsmall');
  background: none;
  border: none;
  cursor: pointer;
  font: inherit;
  color: inherit;
  text-align: left;
}
</style>
